import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createUri, Api } from '@api/utils';

import { Mutation, Query } from './utils';
import { UserUrls } from '@api/user';
import type { CognitoAttrValues, CognitoListUserResponse } from '@/types/cognito';

enum Tag {
    UserList = 'userList',
}

export type User = {
    [key in CognitoAttrValues]: string;
} & {
    Username: string;
    Enabled: boolean;
    UserStatus: string;
    sub: string;
    password?: string;
};

type ListUsersResponse = Omit<CognitoListUserResponse, 'Users'> & { Users: User[] };

export const authHooks = createApi({
    reducerPath: 'authApi',
    tagTypes: Object.values(Tag),
    baseQuery: fetchBaseQuery({
        baseUrl: createUri(Api.Auth, '/auth/V1'),
    }),
    endpoints: (build) => ({
        User: build.mutation<void, { user: User; token: string; method: 'POST' | 'PUT' }>({
            query: ({ user, token, method }) => ({
                url: UserUrls.AdminUsers,
                body: user,
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            invalidatesTags: [Tag.UserList],
        }),
        DeleteUser: build.mutation<void, { token: string; email: string }>({
            query: ({ token, email }) => ({
                url: UserUrls.AdminUsers,
                method: 'DELETE',
                body: { Username: email },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            invalidatesTags: [Tag.UserList],
        }),
        PasswordReset: build.mutation<void, { email: string }>({
            query: ({ email }) => ({
                url: UserUrls.ResetPass,
                params: { email },
                method: 'GET',
            }),
        }),
        user: build.query<User, { token: string; id?: string }>({
            query: ({ token, id }) => {
                const url = `${UserUrls.AdminUsers}?limit=1&searchBy=sub&search=${id}`;

                return {
                    url,
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                };
            },
            transformResponse: ({ Users }: ListUsersResponse) => {
                return Users?.[0];
            },
            keepUnusedDataFor: 0,
            providesTags: [Tag.UserList],
        }),
        listUsers: build.query<
            {
                data: User[];
                total: number;
                limit: number;
                page: {
                    curr: number;
                    prev: number | null;
                    next: number | null;
                };
            },
            { token: string; page: number; limit: number; search?: string }
        >({
            query: ({ token, ...params }) => ({
                url: UserUrls.Users,
                params,
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [Tag.UserList],
        }),
    }),
});

/** Intermediary Wrapper */
const auth = {
    useUserMutation: Mutation.withToken(authHooks.useUserMutation),
    useDeleteUserMutation: Mutation.withToken(authHooks.useDeleteUserMutation),
    usePasswordResetMutation: authHooks.usePasswordResetMutation,
    useUserQuery: Query.withToken(authHooks.useUserQuery),
    useListUsersQuery: Query.withToken(authHooks.useListUsersQuery),
};

export const { useUserMutation, useDeleteUserMutation, usePasswordResetMutation, useListUsersQuery, useUserQuery } =
    auth;
